import { router } from "@inertiajs/react"
import { Badge, Button, Card, Code, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"

import { formatDateTime } from "@/lib/datetime"
import { cancelProjectCampaignExecutionPath, projectCampaignExecutionPath } from "@/routes"
import { CampaignExecutionSchema } from "@/schemas/campaign-execution"
import { ProjectSchema } from "@/schemas/project"

const PropsSchema = z.object({
  executions: z.array(CampaignExecutionSchema),
  project: ProjectSchema,
})

type Props = z.infer<typeof PropsSchema>

export default function RecentCampaignExecutions(props: Props) {
  const { executions, project } = PropsSchema.parse(props)

  const handleCancelExecution = (executionId: number) => {
    if (confirm("Are you sure you want to cancel this campaign execution?")) {
      router.patch(cancelProjectCampaignExecutionPath(project.slug, executionId))
    }
  }

  const handleDeleteExecution = (executionId: number) => {
    if (confirm("Are you sure you want to delete this campaign execution?")) {
      router.delete(projectCampaignExecutionPath(project.slug, executionId))
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge color="green">Completed</Badge>
      case "failed":
        return <Badge color="red">Failed</Badge>
      case "started":
        return <Badge color="blue">In Progress</Badge>
      case "cancelled":
        return <Badge color="orange">Cancelled</Badge>
      default:
        return <Badge color="gray">{status}</Badge>
    }
  }

  const formatStepProgress = (currentIndex: number | null, totalSteps: number) => {
    if (currentIndex === null) return "-"
    return `${currentIndex + 1}/${totalSteps}`
  }

  if (executions.length === 0) {
    return null
  }

  return (
    <>
      <Flex justify="between" align="center">
        <Heading size="4" weight="medium">
          Recent Campaign Executions
        </Heading>
      </Flex>

      <Card>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Customer</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Campaign</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Progress</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Started</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {executions.map((execution) => (
              <Table.Row key={execution.id}>
                <Table.Cell>
                  <Code>{execution.customer?.email ?? "Unknown"}</Code>
                </Table.Cell>
                <Table.Cell>{execution.campaign_name}</Table.Cell>
                <Table.Cell>{getStatusBadge(execution.status)}</Table.Cell>
                <Table.Cell>
                  <Code>
                    {formatStepProgress(execution.current_step_index, execution.total_steps)}
                  </Code>
                </Table.Cell>
                <Table.Cell>{formatDateTime(execution.created_at)}</Table.Cell>
                <Table.Cell>
                  {execution.error_message ? (
                    <Text size="1" color="red" style={{ wordBreak: "break-word" }}>
                      {execution.error_message}
                    </Text>
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  {execution.status === "started" ? (
                    <Button
                      size="1"
                      variant="outline"
                      color="orange"
                      onClick={() => handleCancelExecution(execution.id)}
                    >
                      Cancel
                    </Button>
                  ) : execution.status === "completed" ||
                    execution.status === "failed" ||
                    execution.status === "cancelled" ? (
                    <Button
                      size="1"
                      variant="outline"
                      color="red"
                      onClick={() => handleDeleteExecution(execution.id)}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Card>
    </>
  )
}
